<template>
  <validation-observer ref="requestServiceApproveForm" v-slot="{}" tag="form">
    <div class="row">
      <div class="col-12 mb-1">
        <label class="color-gray-7 font-16 font-weight-bold">
          <i class="fas fa-check color-green mr-1" />
          Razón de Aprobación
        </label>
      </div>
      <div class="col col-sm-12 col-md-4">
        <validation-provider
          v-if="!isReadOnly"
          v-slot="{ errors }"
          name="approvalReason"
          rules="required"
        >
          <custom-drop-down
            v-model="reasonSelected"
            custom-error-msg="La razón es requerida."
            :error="errors[0]"
            fieldkey="id"
            fieldname="name"
            label="Razón:"
            :options="reasons"
            placeholder="Seleccione"
          />
        </validation-provider>
        <div v-else>
          <p class="data-title mb-0 title">Razón:</p>
          <p class="font-weight-normal description">
            {{ determination.reasonDescription }}
          </p>
        </div>
      </div>
      <div v-if="showCompensatoryServiceCount" class="col col-sm col-12">
        <validation-provider
          v-if="!isReadOnly"
          v-slot="{ errors }"
          name="compensatoryServiceCount"
          rules="required|differentFromZero"
        >
          <label class="data-title mb-1">Cantidad de Servicios:</label>
          <custom-input
            v-model="model.compensatoryServiceCount"
            custom-error-msg=""
            :error="errors[0]"
            style="width:100px;"
            type="number"
          />
          <custom-error
            v-if="errors[0]"
            class="ml-n1"
            message="La cantidad es requerida."
            variant="no-border"
          />
        </validation-provider>
      </div>
      <div v-if="showServiceApprovalJugdeName" class="col-12 col-md-4">
        <validation-provider
          v-if="!isReadOnly"
          v-slot="{ errors }"
          name="approvalJudgeName"
          rules="required"
        >
          <label class="data-title mb-0">Nombre del Juez:</label>
          <custom-input
            v-model="model.judgeName"
            custom-error-msg="El nombre del juez es requerido."
            :error="errors[0]"
          />
        </validation-provider>
      </div>
      <div v-if="determination.judgeName">
        <p class="data-title mb-0 title">Nombre del Juez:</p>
        <p class="font-weight-normal description">
          {{ determination.judgeName }}
        </p>
      </div>
      <div
        v-if="determination.compensatoryServiceCount"
        class="col col-sm col-12"
      >
        <p class="data-title mb-0 title">Cantidad de Servicios:</p>
        <p class="font-weight-normal description">
          {{ determination.compensatoryServiceCount }}
        </p>
      </div>
      <div v-if="showServiceApprovalComment" class="col-12 mt-3">
        <validation-provider
          v-if="!isReadOnly"
          v-slot="{ errors }"
          name="approvalComment"
          rules="required"
        >
          <label class="data-title mb-0">Comentario:</label>
          <custom-text-area
            v-model="model.description"
            :count-classes="['float-right']"
            custom-error-msg="El comentario es requerido."
            :error="errors[0]"
            :maxlength="3000"
            show-char-count
          />
        </validation-provider>
      </div>
      <div v-if="determination.description" class="col-12">
        <p class="data-title mb-0 title">Comentario:</p>
        <p class="font-weight-normal description">
          {{ determination.description }}
        </p>
      </div>
      <div v-if="!isReadOnly" class="col-12 mt-3">
        <div class="d-flex flex-row-reverse bd-highlight w-100 actions">
          <div class="bd-highlight ml-2">
            <action-button
              i="fas  fa-save fa-lg"
              text="Guardar"
              variant="success"
              @click="saveApproval"
            />
          </div>
          <div class="mx-0 bd-highlight">
            <action-button
              id="modal-btn-cancelar"
              i="fas fa-times-circle"
              text="Cancelar"
              variant="outline-danger"
              @click="$emit('cancel')"
            />
          </div>
        </div>
      </div>
    </div>
  </validation-observer>
</template>

<script>
import objectToFormData from 'object-to-formdata';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import CustomDropDown from 'custom-drop-down';
import CustomTextArea from 'CustomTextArea';
import CustomInput from 'CustomInput';
import ActionButton from 'ActionButton';
import CustomError from 'custom-error';
import alert from '@quisit/mipe-utils/mixins/alert.mixin.js';
import ServiceStatuses from '@/utils/constants/student/provisionalRemedyAnswerStatus.js';
import DeterminationReasons from '@/utils/constants/student/determinationReason.js';

import {
  saveDetermination,
  getDeterminationRequestService,
} from '@/modules/student-answer-rp/api.js';

export default {
  name: 'RequestServiceApproval',
  components: {
    CustomDropDown,
    CustomTextArea,
    CustomInput,
    ActionButton,
    CustomError,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [alert],
  props: {
    data: {
      type: Object,
      required: true,
      default: () => {},
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      model: {
        judgeName: null,
        description: null,
      },
      reasonSelected: null,
      determination: {
        requestServiceId: null,
        serviceStatusDescription: null,
        reasonDescription: null,
        judgeName: null,
        description: null,
        objection: null,
        rpRequestServiceDeterminationDocumentId: null,
      },
    };
  },
  computed: {
    showServiceApprovalJugdeName() {
      return (
        this.reasonSelected?.id === DeterminationReasons.AprobadoPorOrderDeJuez
      );
    },
    showCompensatoryServiceCount() {
      return (
        this.reasonSelected?.id ===
        DeterminationReasons.ApprovedByCompensatoryServices
      );
    },
    showServiceApprovalComment() {
      if (!this.reasonSelected) return false;
      return [
        DeterminationReasons.ApprovedByUnitDirector,
        DeterminationReasons.AprobadoGeneral,
        DeterminationReasons.ApprovedByCompensatoryServices,
        DeterminationReasons.ApprovedByReConsiderations,
      ].includes(this.reasonSelected.id);
    },
    reasons() {
      const hasPermissionApprovedByUnitDirector = this.userHasPermissions(
        'menu.rempro.view.reasonforapprovedbyunitdirector'
      );
      const hasPermissionGeneralApprovalReason = this.userHasPermissions(
        'menu.rempro.view.generalapprovalreason'
      );

      let excludedItems = [];

      if (!hasPermissionApprovedByUnitDirector)
        excludedItems.push(DeterminationReasons.ApprovedByUnitDirector);

      if (!hasPermissionGeneralApprovalReason)
        excludedItems.push(DeterminationReasons.AprobadoGeneral);

      return this.data.reasons.filter((x) => !excludedItems.includes(x.id));
    },
  },
  mounted() {
    if (this.isReadOnly) {
      this.getDeterminationRequestService();
    }
  },
  methods: {
    async saveApproval() {
      const isFormValid = await this.$refs.requestServiceApproveForm.validate();
      if (!isFormValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información de aprobación solicitada.',
          'error'
        );
        return;
      }

      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea Aprobar el servicio ${this.data.serviceDiscipline}?`,
        'Sí',
        'No'
      );
      if (!answer) return;

      const payload = objectToFormData(
        {
          requestServiceId: this.data.requestServiceId,
          serviceStatusId: ServiceStatuses.Aprobado,
          reasonId: this.reasonSelected.id,
          ...this.model,
        },
        { indices: true, nullsAsUndefineds: true }
      );

      const { data } = await saveDetermination(payload).catch((error) => {
        this.ShowToast('Alerta', `${error.message}.`, 'error');
        return { data: false };
      });

      if (data)
        this.ShowToast(
          `Confirmación`,
          'El servicio ha sido aprobado exitosamente.',
          'success'
        );

      this.$emit('save', data);
    },
    async getDeterminationRequestService() {
      const { data } = await getDeterminationRequestService(
        this.data.requestServiceId
      ).catch((error) => {
        this.ShowToast('Alerta', `${error.message}.`, 'error');
        return { data: false };
      });

      if (data) {
        this.determination = data;
      }
    },
  },
  watch: {
    isReadOnly(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.getDeterminationRequestService();
      }
    },
  },
};
</script>
<style scoped lang="scss">
.column-left {
  max-width: 300px !important;
}
.title {
  font-size: 14px;
}
.description {
  font-size: 16px;
}
</style>
